'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getBedbugs, type Building } from '@repo/api';
import { PLACEHOLDER_REPORT_DATA } from '../../context/constants';
import { BedbugInfestationSectionSkeleton } from './BedbugInfestationSectionSkeleton';
import { BedbugInfestationSection } from './BedbugInfestationSection';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';

interface BedbugInfestationProps {
  building: Building;
  buildingAddress: string;

  showSkeleton: boolean; // parent (explorer/addReport flow)
  isReportReady: boolean; // true only when addReport done or hasAccess
  shouldBlur?: boolean; // if you still need it elsewhere
}

function BedbugInfestation({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: BedbugInfestationProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [baseBedbugData, setBaseBedbugData] = useState<any | null>(null);
  const [baseLoading, setBaseLoading] = useState(true);
  const [baseError, setBaseError] = useState<string | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_bedbug_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  // reset when building change

  useEffect(() => {
    // keep skeleton up until addReport is ready
    if (!isReportReady) {
      setBaseLoading(false);
      setBaseBedbugData(PLACEHOLDER_REPORT_DATA);
      return;
    }

    (async () => {
      setBaseLoading(true);
      setBaseError(null);

      try {
        const response = await getBedbugs({ buildingId: building.id });
        setBaseBedbugData(response);
      } catch (err: any) {
        setBaseError(err?.message || 'Failed to load bedbug data');
      } finally {
        setBaseLoading(false);
      }
    })();
  }, [isReportReady, building.id]);

  // ✅ Single skeleton condition
  const shouldShowSkeleton = showSkeleton || baseLoading;

  if (shouldShowSkeleton) {
    return <BedbugInfestationSectionSkeleton />;
  }

  return (
    <div ref={sectionRef}>
      <BedbugInfestationSection
        buildingId={building.id}
        buildingSlug={building.slug}
        buildingAddress={buildingAddress}
        isBlurRequired={!isReportReady}
        baseBedbugData={baseBedbugData}
        baseLoading={baseLoading}
        baseError={baseError}
      />
    </div>
  );
}

export default BedbugInfestation;
